import { ref, computed, inject, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { CONFIG } from '../config';
import { publishedPropertyFingerprints } from '../i18n/publishedBundles.js';
import { syncPublishedPropertyContentIfNeeded } from '../utils/cmsContentRemoteSync.js';
import {
  hasPublishedPropertyContent,
  normalizePropertyWire
} from '../utils/normalizePropertyWire.js';
import { shouldSyncPublicCmsFromApi } from '../utils/publicCmsSource.js';
import { getPublicPropertyPayload } from '../utils/publicPropertyPayload.js';

const pendingContentByLocale = new Map();

function isEmptyPropertyBody(body) {
  if (!body || typeof body !== 'object') return false;
  if (body.success === false) return false;
  return body.property === null || body.property === undefined;
}

export function useLocalePropertyContent() {
  const initialState = inject('initialState', {});
  const publishedByLocale = inject('publishedPropertyContentByLocale', {});
  const route = useRoute();

  const locale = computed(() => {
    const lang = route.params?.lang;
    if (lang && CONFIG.SUPPORTED_LOCALES.includes(lang)) {
      return lang;
    }
    return initialState?.locale || CONFIG.DEFAULT_LOCALE;
  });

  const embeddedContent = computed(() => {
    if (hasPublishedPropertyContent(initialState?.property)) {
      const normalized = normalizePropertyWire({
        property: initialState.property,
        floors: initialState.floors || []
      });
      if (normalized) return normalized;
    }
    const loc = locale.value;
    const pub = publishedByLocale?.[loc];
    if (hasPublishedPropertyContent(pub?.property)) {
      return normalizePropertyWire(pub);
    }
    return null;
  });

  const fetchedContent = ref(null);
  const emptyConfirmed = ref(false);
  const loadingRemote = ref(false);
  const remoteError = ref(null);
  let fetchSeq = 0;

  const content = computed(() => {
    if (emptyConfirmed.value) {
      return { property: null, floors: [] };
    }
    return fetchedContent.value || embeddedContent.value;
  });

  const isEmptyProperty = computed(
    () => emptyConfirmed.value || (content.value != null && !content.value.property)
  );

  function clearInitialStateProperty() {
    if (initialState && typeof initialState === 'object') {
      initialState.property = null;
      initialState.floors = [];
    }
  }

  function confirmEmptyProperty(loc) {
    emptyConfirmed.value = true;
    fetchedContent.value = null;
    clearInitialStateProperty();
    remoteError.value = null;
    if (initialState && typeof initialState === 'object') {
      initialState.locale = loc;
    }
  }

  function applyContentBody(loc, body) {
    if (isEmptyPropertyBody(body)) {
      confirmEmptyProperty(loc);
      return true;
    }
    if (body?.property && !hasPublishedPropertyContent(body.property)) {
      confirmEmptyProperty(loc);
      return true;
    }

    const normalized = normalizePropertyWire(body);
    if (!normalized) {
      if (body && body.success !== false) {
        confirmEmptyProperty(loc);
        return true;
      }
      return false;
    }

    emptyConfirmed.value = false;
    fetchedContent.value = normalized;
    if (initialState && typeof initialState === 'object') {
      initialState.property = body.property || normalized.property;
      if (body.property?.manager) {
        initialState.property.manager = body.property.manager;
      }
      initialState.floors = normalized.floors;
      initialState.locale = loc;
    }
    return true;
  }

  async function fetchPropertyFromApi(loc) {
    try {
      const data = await getPublicPropertyPayload(loc);
      return { ok: true, body: data, error: null };
    } catch (e) {
      return { ok: false, body: null, error: e?.message || 'No property content for this language' };
    }
  }

  async function fetchRemote() {
    if (typeof window === 'undefined') return;
    const loc = locale.value;

    if (!shouldSyncPublicCmsFromApi()) {
      const bundled = publishedByLocale?.[loc];
      if (bundled?.property && applyContentBody(loc, bundled)) return;
      if (!embeddedContent.value && (bundled?.property === null || bundled?.property === undefined)) {
        confirmEmptyProperty(loc);
        return;
      }
      if (!embeddedContent.value) {
        remoteError.value = 'No property data available';
      }
      return;
    }

    const bundledFp = publishedPropertyFingerprints[loc] || '';

    let syncedBody = null;
    await syncPublishedPropertyContentIfNeeded({
      locale: loc,
      bundledFingerprint: bundledFp,
      bundledContent: publishedByLocale?.[loc],
      onUpdate: (l, body) => {
        syncedBody = body;
        applyContentBody(l, body);
      }
    });

    if (syncedBody) {
      return;
    }

    const seq = ++fetchSeq;

    let promise = pendingContentByLocale.get(loc);
    if (!promise) {
      promise = fetchPropertyFromApi(loc).finally(() => {
        pendingContentByLocale.delete(loc);
      });
      pendingContentByLocale.set(loc, promise);
    }

    loadingRemote.value = true;
    remoteError.value = null;
    try {
      const result = await promise;
      if (seq !== fetchSeq || locale.value !== loc) return;
      if (result.ok) {
        applyContentBody(loc, result.body);
      } else {
        fetchedContent.value = null;
        if (!embeddedContent.value) remoteError.value = result.error;
      }
    } catch (e) {
      if (seq !== fetchSeq || locale.value !== loc) return;
      fetchedContent.value = null;
      if (!embeddedContent.value) {
        remoteError.value = e.message || 'Failed to load property content';
      }
    } finally {
      if (seq === fetchSeq) loadingRemote.value = false;
    }
  }

  let mounted = false;
  onMounted(() => {
    mounted = true;
    fetchRemote();
  });

  watch(locale, (newLoc, oldLoc) => {
    if (!mounted || oldLoc === undefined || newLoc === oldLoc) return;
    fetchedContent.value = null;
    emptyConfirmed.value = false;
    if (!shouldSyncPublicCmsFromApi()) {
      const bundled = publishedByLocale?.[newLoc];
      if (bundled?.property) applyContentBody(newLoc, bundled);
      else if (bundled?.property === null) confirmEmptyProperty(newLoc);
      return;
    }
    clearInitialStateProperty();
    fetchRemote();
  });

  const errorMessage = computed(() => {
    if (isEmptyProperty.value) return null;
    if (content.value?.property) return null;
    return remoteError.value || 'No property data available';
  });

  const isLoading = computed(() => {
    if (isEmptyProperty.value) return false;
    if (content.value?.property) return false;
    if (loadingRemote.value) return true;
    if (remoteError.value) return false;
    return !embeddedContent.value;
  });

  return {
    content,
    locale,
    isLoading,
    isEmptyProperty,
    errorMessage,
    refetch: fetchRemote
  };
}
